import React, { useEffect, useRef, useState } from "react";
import { Add as AddIcon, Clear as ClearIcon, Delete as DeleteIcon } from "../../_core/components/icons";
import { makeStyles, UIBox, UIIconButton, UIStack } from "../../_core/components";
import Main from "../../_core/components/layout/Main";
import UITable from "../../_core/components/ui/UITable";
import UIButton from "../../_core/components/ui/UIButton";
import UISearch from "../../_core/components/ui/UISearch";
import UIEmailWithVerification from "../../_core/components/ui/UIEmailWithVerification";
import { useApiAlerts, useTableControls } from "../../_core/hooks";
import { formatDate } from "../../_core/utils";
import { useDeletePartnerUser, usePartnerUsers } from "../hooks";
import { PartnerUser } from "../api/types";
import CreateUserModal from "../components/CreateUserModal";
import DeleteUserDialog from "../components/DeleteUserDialog";
import PartnerUserActionsDropdown from "../components/PartnerUserActionsDropdown";

const PartnerUsers = () => {
  const classes = useStyles();
  const controls = useTableControls();
  
  const [emailFilter, setEmailFilter] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<PartnerUser | null>(null);

  // Debounce email filter
  const debounceTimeout = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      setDebouncedEmail(emailFilter);
      controls.setPage(0); // Reset to first page when filtering
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFilter]); // Removed controls from dependencies to prevent infinite re-renders

  const { data, isFetching, isError: isQueryError, error: queryError } = usePartnerUsers({
    page: controls.page,
    size: controls.rowsPerPage,
    email: debouncedEmail || undefined,
  });

  const {
    mutate: executeDelete,
    isSuccess: isDeleteSuccess,
    error: deleteError,
    isError: isDeleteError,
  } = useDeletePartnerUser();

  useApiAlerts(
    isDeleteSuccess ? "User deleted successfully" : undefined,
    queryError || deleteError,
    isQueryError || isDeleteError
  );

  const handleDeleteClick = (user: PartnerUser) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      executeDelete(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };


  return (
    <Main
      title="Partner Users"
      data-testid="partner-users-container"
      action={{
        label: "Create User",
        startIcon: <AddIcon />,
        onClick: () => setCreateModalOpen(true),
      }}
    >
      <div className={classes.root}>
        <UIBox className={classes.filterSection}>
          <UIBox className={classes.searchContainer}>
            <UISearch
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="Search by exact email..."
              className={classes.searchInput}
              debounceMs={500}
            />
            {emailFilter && (
              <UIIconButton
                onClick={() => setEmailFilter("")}
                size="small"
                className={classes.clearButton}
                title="Clear search"
              >
                <ClearIcon fontSize="small" />
              </UIIconButton>
            )}
          </UIBox>
        </UIBox>

        <UITable
          className={classes.table}
          isLoading={isFetching}
          columns={["Name", "Email", "Created", "Last Activity", "Actions"]}
          data={data?.content}
          rowCallback={(user: PartnerUser) => [
            user.name,
            <UIEmailWithVerification
              key={`email-${user.id}`}
              email={user.email}
              isValidated={user.confirmed}
              emailDeliveryFailure={user.emailDeliveryFailure}
            />,
            formatDate(user.createdAt),
            formatDate(user.lastActivity),
            <UIStack key={`actions-${user.id}`} direction="row" alignItems="center" gap={1}>
              <UIButton
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick(user)}
              >
                Delete
              </UIButton>
              <PartnerUserActionsDropdown user={user} />
            </UIStack>,
          ]}
          options={{
            ...controls,
            count: data?.totalElements,
          }}
        />
      </div>

      <CreateUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />

      <DeleteUserDialog
        open={deleteDialogOpen}
        user={userToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Main>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 8,
  },
  filterSection: {
    marginBottom: 24,
  },
  searchContainer: {
    position: "relative",
    maxWidth: 400,
  },
  searchInput: {
    width: "100%",
    paddingRight: 40, // Always reserve space for clear button
  },
  clearButton: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    color: theme.palette.action.active,
    "&:hover": {
      color: theme.palette.action.hover,
      backgroundColor: theme.palette.action.hover + "20",
    },
  },
  table: {
    paddingTop: 22,
  },
}));

export default PartnerUsers;